// Driver documents › Verification letter (wave 3, W2_Portal_Settings look):
// pick a driver, check their details, documents and compliance on cards,
// choose the letter's language and generate it (one primary button).
//
// Data, exactly as before: the user list (fetchUsersOnce, only for people
// allowed here), the secure document links for the picked driver
// (getSecureDocumentUrl), the letter itself (generateDriverLetter, same
// text and numbering) and one audit record per letter (saveLetterRecord,
// same fields). Access, as before: admin, finance, call-centre supervisor
// and agent, and fleet admins whose source is oromiabank. Phone, email and
// TIN are hidden on screen in demo mode (the letter is unchanged).
//
// Also exports DriverPicker, the "Select driver" box used by the clearance
// letter too. Call-centre feedback (29 Oct 2026): its X (clear) shows as
// soon as something is typed or picked, not only while the pointer is on it.
import React, { useState, useEffect, useContext } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api, FirebaseContext, store } from 'common';
import { getSecureDocumentUrl, extractStoragePath } from '../utils/securityUtils';
import { generateDriverLetter } from '../utils/driverLetterGenerator';
import { saveLetterRecord } from '../utils/letterRecords';
import { tokens as c } from '../theme';
import { InlineMessage } from '../components/feedback';
import { DetailCard, InfoRow, SectionCard, StatusChip, FieldLabel, calmCaps, formatPhoneDisplay } from '../components/modern';

export const LETTER_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'om', name: 'Afaan Oromoo (Oromo)' },
  { code: 'am', name: 'አማርኛ (Amharic)' },
  { code: 'ti', name: 'ትግርኛ (Tigrinya)' },
  { code: 'ar', name: 'العربية (Arabic)' },
];

/**
 * The drivers of the loaded list as picker options (the same objects as
 * before: the driver, uid and a `desc` to search by). Phone and email are in
 * `desc` only when private data may be shown (demo mode never matches them).
 */
export const driverOptionsOf = (staticusers, t, allowPrivate) =>
  (staticusers || [])
    .filter((user) => user.usertype === 'driver')
    .map((driver) => ({
      ...driver,
      uid: driver.id,
      desc: allowPrivate
        ? `${driver.firstName} ${driver.lastName} (${driver.mobile || t('no_mobile', { defaultValue: 'No mobile' })}) ${driver.email || ''} ${driver.vehicleNumber ? `- ${driver.vehicleNumber}` : ''}`
        : `${driver.firstName} ${driver.lastName} ${driver.vehicleNumber ? `- ${driver.vehicleNumber}` : ''}`,
    }));

/** "Select driver": one label above a searchable list of the loaded drivers, with an X that clears it in one click. */
export function DriverPicker({ id, label, drivers, value, onChange }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ maxWidth: 680 }}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {drivers.length > 0 ? (
        <Autocomplete
          id={id}
          value={value}
          options={drivers}
          onChange={onChange}
          autoHighlight
          clearText={t('clear_search', { defaultValue: 'Clear search' })}
          // MUI shows the X only under the pointer or while typing; keep it
          // in sight whenever the box holds something.
          sx={{ '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' } }}
          getOptionLabel={(option) => (option && option.desc) || ''}
          isOptionEqualToValue={(option, v) => option.desc === v.desc}
          renderOption={(props, option) => (
            <li {...props} key={option.id || option.desc}>
              {option.desc}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('search_name_or_phone', { defaultValue: 'Search by name or phone…' })}
              inputProps={{ ...params.inputProps, autoComplete: 'off' }}
            />
          )}
        />
      ) : (
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: c.INK_2, fontSize: 14 }}>
          <CircularProgress size={16} color="inherit" />
          {t('loading_drivers', { defaultValue: 'Loading drivers…' })}
        </Box>
      )}
    </Box>
  );
}

// One document picture (or "not uploaded"), with its name above it.
function DocumentImage({ title, src, loading }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, color: c.INK_2, mb: 0.75 }}>
        {title}
      </Typography>
      {src ? (
        <Box
          component="img"
          src={src}
          alt={title}
          sx={{
            display: 'block',
            width: '100%',
            aspectRatio: '5 / 4',
            objectFit: 'cover',
            borderRadius: '12px',
            border: `1px solid ${c.OUTLINE}`,
            bgcolor: c.SURFACE_ALT,
            opacity: loading ? 0.6 : 1,
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            aspectRatio: '5 / 4',
            borderRadius: '12px',
            border: `1px dashed ${c.OUTLINE_STRONG}`,
            bgcolor: c.SURFACE_ALT,
            color: c.TEXT_3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            textAlign: 'center',
            px: 1,
          }}
        >
          <ImageNotSupportedOutlinedIcon aria-hidden="true" sx={{ fontSize: 36 }} />
          <Typography sx={{ fontSize: 13 }}>{t('no_document_uploaded', { defaultValue: 'Document Not Uploaded' })}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default function DriverLetterGenerator() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { fetchUsersOnce } = api;

  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const firebase = useContext(FirebaseContext);
  const { auth: firebaseAuth, config } = firebase;
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [secureUrls, setSecureUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Who may use this page (worked out on every render, so the page never
  // flashes "no permission" before the check): admin, finance,
  // callcentersupervisor, callcenteragent, and fleetadmins only if source is
  // 'oromiabank'.
  const userType = auth.profile?.usertype;
  const userSource = auth.profile?.source;
  const hasAccess = ['admin', 'finance', 'callcentersupervisor', 'callcenteragent'].includes(userType) || (userType === 'fleetadmin' && userSource === 'oromiabank');

  useEffect(() => {
    if (hasAccess) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, hasAccess]);

  useEffect(() => {
    if (staticusers) {
      setDrivers(driverOptionsOf(staticusers, t, allowPrivate));
    }
  }, [staticusers, t, allowPrivate]);

  const checkCompliance = (driver) => {
    const hasCar = driver.vehicleNumber && driver.carType && driver.vehicleMake && driver.vehicleModel;
    const hasLicense = driver.licenseImage && driver.licenseImageBack;
    const hasTIN = driver.tin;
    const isFullyCompliant = hasCar && hasLicense && hasTIN;

    return {
      hasCar,
      hasLicense,
      hasTIN,
      isFullyCompliant,
    };
  };

  const handleDriverSelect = (event, newValue) => {
    setSelectedDriver(newValue);
    setSecureUrls({}); // Reset secure URLs when driver changes
  };

  // Fetch secure URLs for PII documents when driver is selected
  useEffect(() => {
    const fetchSecureUrls = async () => {
      if (!selectedDriver || !selectedDriver.id || !firebaseAuth || !firebaseAuth.currentUser) return;

      setLoadingUrls(true);
      const urls = {};

      try {
        const idToken = await firebaseAuth.currentUser.getIdToken();

        // List of PII document fields to fetch secure URLs for
        const piiFields = [
          { field: 'licenseImage', url: selectedDriver.licenseImage },
          { field: 'licenseImageBack', url: selectedDriver.licenseImageBack },
          { field: 'insuranceImage', url: selectedDriver.insuranceImage },
        ];

        for (const { field, url } of piiFields) {
          if (url) {
            const storagePath = extractStoragePath(url);
            if (storagePath) {
              const secureUrl = await getSecureDocumentUrl(selectedDriver.id, storagePath, idToken, store, config);
              if (secureUrl) {
                urls[field] = secureUrl;
              }
            }
          }
        }

        setSecureUrls(urls);
      } catch (err) {
        console.error('Error fetching secure URLs:', err);
      } finally {
        setLoadingUrls(false);
      }
    };

    fetchSecureUrls();
  }, [selectedDriver, firebaseAuth, config]);

  // Use secure URLs if available, fallback to original URLs
  const getDisplayUrl = (field) => {
    return secureUrls[field] || selectedDriver?.[field];
  };

  const handleGeneratePDF = async () => {
    if (!selectedDriver) {
      setError(t('please_select_driver', { defaultValue: 'Please select a driver first' }));
      return;
    }
    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      const refNo = await generateDriverLetter(selectedDriver, selectedLanguage, checkCompliance);
      // Fraud-audit trail: record every issued letter (who/what/when).
      const compliance = checkCompliance(selectedDriver);
      const profile = auth.profile || {};
      await saveLetterRecord(firebase, {
        type: 'verification',
        refNo: refNo || null,
        language: selectedLanguage,
        driverId: selectedDriver.id || selectedDriver.uid || null,
        driverName: `${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim(),
        driverMobile: selectedDriver.mobile || null,
        driverEmail: selectedDriver.email || null,
        tin: selectedDriver.tin || null,
        plate: selectedDriver.vehicleNumber || null,
        compliance: {
          hasCar: !!compliance.hasCar,
          hasLicense: !!compliance.hasLicense,
          hasTIN: !!compliance.hasTIN,
          isFullyCompliant: !!compliance.isFullyCompliant,
        },
        generatedByUid: profile.uid || null,
        generatedByName: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || null,
        generatedByEmail: profile.email || null,
        generatedByRole: profile.usertype || null,
        generatedAt: new Date().getTime(),
      });
      setSuccess(
        t('letter_generated_for', {
          name: `${selectedDriver.firstName} ${selectedDriver.lastName}`,
          defaultValue: 'Letter generated for {{name}}',
        })
      );
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('letter_generate_failed', { defaultValue: 'Could not generate the letter. Please try again.' }));
    } finally {
      setGenerating(false);
    }
  };

  const hidden = t('hidden_demo');
  const statusChip = (ok, okLabel) => (
    <StatusChip tone={ok ? 'success' : 'warning'} icon={ok ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />} label={ok ? okLabel : t('pending')} />
  );

  if (!hasAccess) {
    return (
      <InlineMessage kind="error" title={t('unauthorized_access', { defaultValue: 'Unauthorized Access' })} sx={{ my: 2.5 }}>
        {t('no_page_permission', { defaultValue: 'You do not have permission to access this page.' })}
      </InlineMessage>
    );
  }

  const compliance = selectedDriver ? checkCompliance(selectedDriver) : null;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error ? (
        <InlineMessage kind="error" onClose={() => setError('')}>
          {error}
        </InlineMessage>
      ) : null}
      {success ? (
        <InlineMessage kind="success" onClose={() => setSuccess('')}>
          {success}
        </InlineMessage>
      ) : null}

      <SectionCard icon={<PersonSearchOutlinedIcon />} hue="info" title={t('driver_letter_generator', { defaultValue: 'Driver Verification Letter Generator' })} id="verification-driver">
        <Box sx={{ pt: 1 }}>
          <DriverPicker
            id="verification-driver-picker"
            label={t('select_driver', { defaultValue: 'Select Driver' })}
            drivers={drivers}
            value={selectedDriver}
            onChange={handleDriverSelect}
          />
        </Box>
      </SectionCard>

      {selectedDriver ? (
        <>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
            <DetailCard icon={<BadgeOutlinedIcon />} hue="info" title={t('driver_details', { defaultValue: 'Driver Details' })}>
              <InfoRow label={t('name', { defaultValue: 'Name' })}>{`${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim()}</InfoRow>
              <InfoRow label={t('mobile')}>{allowPrivate ? formatPhoneDisplay(selectedDriver.mobile) : hidden}</InfoRow>
              <InfoRow label={t('email')}>{allowPrivate ? selectedDriver.email || '—' : hidden}</InfoRow>
              <InfoRow label={t('tin_number')}>{allowPrivate ? selectedDriver.tin || '—' : hidden}</InfoRow>
              {selectedDriver.vehicleNumber ? (
                <InfoRow label={t('vehicle')}>{`${selectedDriver.vehicleNumber} (${selectedDriver.carType || '—'})`}</InfoRow>
              ) : null}
            </DetailCard>

            <DetailCard icon={<FactCheckOutlinedIcon />} hue="success" title={t('compliance_status')}>
              <InfoRow label={t('letter_vehicle_registration', { defaultValue: 'Vehicle registration' })}>{statusChip(compliance.hasCar, t('complete'))}</InfoRow>
              <InfoRow label={t('letter_drivers_license', { defaultValue: "Driver's license" })}>
                {statusChip(compliance.hasLicense, t('letter_verified', { defaultValue: 'Verified' }))}
              </InfoRow>
              <InfoRow label={t('tin_number')}>{statusChip(compliance.hasTIN, t('letter_registered', { defaultValue: 'Registered' }))}</InfoRow>
              <InfoRow label={t('letter_overall_status', { defaultValue: 'Overall status' })}>
                <StatusChip
                  tone={compliance.isFullyCompliant ? 'success' : 'amber'}
                  icon={compliance.isFullyCompliant ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />}
                  label={compliance.isFullyCompliant ? t('fully_compliant') : t('letter_partly_compliant', { defaultValue: 'Partly compliant' })}
                />
              </InfoRow>
            </DetailCard>
          </Box>

          <SectionCard
            icon={<FolderOpenOutlinedIcon />}
            hue="amber"
            title={calmCaps(t('documents_title', { defaultValue: 'Documents' }))}
            id="verification-documents"
            action={loadingUrls ? <CircularProgress size={18} aria-label={t('loading', { defaultValue: 'Loading…' })} /> : null}
          >
            <Box sx={{ display: 'grid', gap: 2, pt: 1, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
              <DocumentImage title={t('license_image_front', { defaultValue: 'License Front' })} src={selectedDriver.licenseImage ? getDisplayUrl('licenseImage') : null} loading={loadingUrls} />
              <DocumentImage title={t('license_image_back', { defaultValue: 'License Back' })} src={selectedDriver.licenseImageBack ? getDisplayUrl('licenseImageBack') : null} loading={loadingUrls} />
              <DocumentImage title={t('insurance_image', { defaultValue: 'Insurance' })} src={selectedDriver.insuranceImage ? getDisplayUrl('insuranceImage') : null} loading={loadingUrls} />
            </Box>
          </SectionCard>

          <SectionCard icon={<DescriptionOutlinedIcon />} hue="accent" title={t('verification_letter', { defaultValue: 'Verification Letter' })} id="verification-letter">
            <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, pt: 1 }}>
              <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                <FieldLabel htmlFor="verification-language">{t('lang')}</FieldLabel>
                <TextField id="verification-language" select fullWidth value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
                  {LETTER_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DownloadRoundedIcon />}
                onClick={handleGeneratePDF}
                disabled={generating}
                sx={{ marginInlineStart: 'auto' }}
              >
                {generating ? t('generating', { defaultValue: 'Generating…' }) : t('generate_letter', { defaultValue: 'Generate letter' })}
              </Button>
            </Box>
          </SectionCard>
        </>
      ) : null}
    </Box>
  );
}
